import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, ShoppingBag, Clock, ChevronDown, Loader2, Check } from 'lucide-react';
import { Lightbulb } from 'lucide-react';
import { MOMENTS } from '../constants';

export const StageOverlay = ({
  selectedGarment,
  tryOnCache,
  isInCart,
  canTryOn,
  session,
  momentsPopupOpen,
  setMomentsPopupOpen,
  momentsLoading,
  generatedMoments,
  onTryOn,
  onAddToCart,
  onGenerateMoment,
}) => {
  if (!selectedGarment) return null;

  const productId = selectedGarment.product_id || selectedGarment.id;
  const hasTryOn = tryOnCache[productId];
  const inCart = isInCart(productId);
  const tryonsUsed = session?.tryons_used || 0;
  const maxTryons = session?.max_tryons || 5;
  const canTryOnMore = canTryOn && (tryonsUsed < maxTryons || hasTryOn);

  return (
    <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between z-10">
      {/* Left: Moments popup */}
      <div className="relative">
        {hasTryOn && (
          <>
            <AnimatePresence>
              {momentsPopupOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  transition={{ type: 'spring', damping: 25, stiffness: 400 }}
                  className="absolute bottom-full left-0 mb-2 flex flex-col gap-1.5"
                >
                  {MOMENTS.map((moment, index) => {
                    const isLoading = momentsLoading[moment.id];
                    const isGenerated = generatedMoments?.[moment.id];

                    return (
                      <motion.button
                        key={moment.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ delay: index * 0.05 }}
                        onClick={(e) => { e.stopPropagation(); onGenerateMoment(moment.id); }}
                        disabled={isLoading}
                        className={`flex items-center gap-2 px-3 py-2 rounded-full text-xs font-medium shadow-lg transition-all whitespace-nowrap ${
                          isGenerated
                            ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white'
                            : 'bg-white/95 backdrop-blur-sm text-slate-700 hover:bg-white hover:scale-105 border border-white/20'
                        }`}
                      >
                        {isLoading ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <span className="text-sm">{moment.icon}</span>
                        )}
                        <span>{moment.label}</span>
                        {isGenerated && <Check className="w-3 h-3 ml-0.5" />}
                      </motion.button>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>

            <motion.button
              onClick={(e) => { e.stopPropagation(); setMomentsPopupOpen(!momentsPopupOpen); }}
              animate={{ backgroundColor: momentsPopupOpen ? 'rgba(251, 191, 36, 0.2)' : 'rgba(255, 255, 255, 0.95)' }}
              className={`flex items-center gap-1.5 px-3 py-2 backdrop-blur-sm rounded-full text-xs font-medium shadow-lg transition-all border border-white/20 ${
                momentsPopupOpen ? 'text-amber-700' : 'text-slate-700 hover:bg-white hover:scale-105'
              }`}
              data-testid="moments-overlay-btn"
            >
              <motion.div animate={{ rotate: momentsPopupOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                <Lightbulb className={`w-3.5 h-3.5 ${momentsPopupOpen ? 'text-amber-600' : 'text-amber-500'}`} />
              </motion.div>
              <span>Moments</span>
              <motion.div animate={{ rotate: momentsPopupOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                <ChevronDown className="w-3 h-3" />
              </motion.div>
            </motion.button>
          </>
        )}
      </div>

      {/* Right: Try On or Add to Cart */}
      <div>
        {!hasTryOn ? (
          <button
            onClick={(e) => { e.stopPropagation(); onTryOn(); }}
            className={`flex items-center gap-1.5 px-4 py-2.5 rounded-full text-xs font-semibold shadow-lg transition-all ${
              canTryOnMore
                ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white hover:from-blue-700 hover:to-blue-600 hover:scale-105'
                : 'bg-amber-50/95 backdrop-blur-sm border border-amber-300/70 text-amber-700 hover:bg-amber-100/95 hover:scale-105 cursor-pointer'
            }`}
            data-testid="try-on-overlay-button"
          >
            {canTryOnMore ? <Sparkles className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
            {canTryOnMore ? 'Try it on Me' : 'Limit Reached'}
          </button>
        ) : (
          <button
            onClick={(e) => { e.stopPropagation(); if (!inCart) onAddToCart(selectedGarment); }}
            className={`flex items-center gap-1.5 px-4 py-2.5 rounded-full text-xs font-semibold shadow-lg transition-all ${
              inCart
                ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white'
                : 'bg-white/95 backdrop-blur-sm text-slate-800 hover:bg-white hover:scale-105 border border-white/20'
            }`}
            data-testid="add-to-cart-overlay-button"
          >
            <ShoppingBag className="w-3.5 h-3.5" />
            {inCart ? 'Added ✓' : 'Add to Cart'}
          </button>
        )}
      </div>
    </div>
  );
};
